import { Clickable } from '@carvana/merch-components';
import { useCallback, useContext } from 'react';
import CvnaTier from '~/constants/vdp/cvnaTier';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useModalContext } from '~/context/vdp/ModalContext/ModalContext';
import ShowcaseContext from '~/context/vdp/ShowcaseContext';
import { CvnaCategory } from '~/contracts/vdp/Features';
import {
  CardContainer,
  CardSVG,
  CardTitle,
  List,
  ListItem
} from './CategoryCard.styles';
import { categoryIconsMobile, categoryIconsDesktop } from './constants';

type OwnProps = {
  category: CvnaCategory;
  index: number;
  inModal?: 'inKeyFeaturesModal' | 'inFeatureAndSpecModal';
};

export default function CategoryCard({ category, index, inModal }: OwnProps) {
  const { name: categoryName, cvnaFeatures } = category;
  const { setKeyFeaturesModalOpen } = useModalContext();
  const { setActiveCategory } = useContext(ShowcaseContext);
  const isMobile = useIsMobile();
  const isOnPage = !inModal;
  const inKeyFeaturesModal = inModal === 'inKeyFeaturesModal';
  const inSpecsModal = inModal === 'inFeatureAndSpecModal';

  const alphaSortedFeatures = cvnaFeatures.sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const iconToUse = isMobile ? categoryIconsMobile : categoryIconsDesktop;

  // TODO cleanup - index can be removed here after vdp-info-showcase2 is cleaned up and REO-2718 is completed
  const handleClick = useCallback(() => {
    setActiveCategory(categoryName);
    setKeyFeaturesModalOpen(true, index);
  }, [setActiveCategory, setKeyFeaturesModalOpen, categoryName, index]);

  return (
    <CardContainer onPage={isOnPage}>
      <CardSVG
        alt={categoryName}
        src={`${process.env.CVNA_APP_CDN_ASSETS_PATH}${iconToUse[categoryName]}`}
      />
      <div>
        <CardTitle biggerTitle={inKeyFeaturesModal}>{categoryName}</CardTitle>

        {inKeyFeaturesModal && (
          <div className="flex flex-col gap-2xl">
            {alphaSortedFeatures.map(feature => {
              const { tier, name, description } = feature;

              return tier === CvnaTier.standout ? (
                <div key={name} className="flex flex-col gap-md">
                  <p className="text-blue-6 tl-header-m">{name}</p>
                  <p className="text-grey-5 tl-body-m">{description}</p>
                </div>
              ) : null;
            })}
          </div>
        )}

        {inSpecsModal && (
          <List>
            {alphaSortedFeatures.map(feature => {
              const { name } = feature;

              return <ListItem key={name}>{name}</ListItem>;
            })}
          </List>
        )}

        {isOnPage && (
          <>
            <List>
              {alphaSortedFeatures.map(feature => {
                const { tier, name } = feature;

                return tier === CvnaTier.standout ? (
                  <ListItem key={name}>{name}</ListItem>
                ) : null;
              })}
            </List>

            <Clickable
              data-analytics-id="Key Features - Details"
              data-analytics-attributes={JSON.stringify({
                openTo: categoryName
              })}
              className="mt-xl w-fit text-blue-2 tl-label-m"
              onClick={handleClick}
            >
              Details
            </Clickable>
          </>
        )}
      </div>
    </CardContainer>
  );
}
